import React, { useState, useRef, useEffect } from "react";
import {
  Flex,
  ButtonGroup,
  Box,
  Avatar,
  Button,
  useDisclosure,
  Text,
  VStack,
  useToast,
  Divider,
  Heading,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  InputGroup,
  Input,
  InputRightElement,
  IconButton,
  Spinner,
  useOutsideClick,
  List,
  ListItem,
  Image,
  Badge,
  HStack,
} from "@chakra-ui/react";
import { BsPerson, BsCameraVideoFill } from "react-icons/bs";
import { FaList, FaClock, FaHistory, FaFilm, FaSitemap, FaSearch, FaArrowRight, FaComments, FaUsers, FaVideo, FaNewspaper, FaBell } from "react-icons/fa";
import { ImSearch } from "react-icons/im";
import { MdFavorite, MdAppShortcut } from "react-icons/md";
import { useNavigate, useLocation } from "react-router-dom";
import { Signin } from "./Signin";
import Signup from "./Signup";
import { useDispatch, useSelector } from "react-redux";
import { logout as logoutAction } from "../../../Redux/Login/action";
import { toggleChatPanel } from '../../../Redux/Chat/action';
import { verifyAuthState } from '../../../utils/authHelper';
import apiClient from '../../../utils/apiConfig';
import { motion, AnimatePresence } from "framer-motion";
import { store } from "../../../Redux/store";
import AdminPanel from "../../Admin/AdminPanel";
import UserDrawer from "../../Profile/UserDrawer";
import CommunityModal from '../../../admin-components/User/CommunityModal';
import FeedbackModal from '../../User/FeedbackModal';
import { buildVideoUrl } from '../../../utils/slugify';
import "./Navbar.css";

// Custom component for mode menu items
const ModeMenuItem = ({ icon, label, description, colorScheme, isActive, onClick }) => {
  return (
    <Box
      as="button"
      display="flex"
      alignItems="center"
      width="100%"
      py={{ base: 2, md: 2.5 }}
      px={{ base: 3, md: 4 }}
      borderRadius="md"
      onClick={onClick}
      bg={isActive ? `${colorScheme}.700` : "transparent"}
      _hover={{
        bg: isActive ? `${colorScheme}.600` : "whiteAlpha.100",
        transform: "translateY(-1px)",
      }}
      _active={{
        bg: `${colorScheme}.800`,
        transform: "translateY(0px)",
      }}
      transition="all 0.2s"
      position="relative"
      overflow="hidden"
    >
      {isActive && (
        <Box 
          position="absolute"
          left={0}
          top="50%"
          transform="translateY(-50%)"
          width={{ base: "3px", md: "4px" }}  // Smaller indicator on mobile
          height="70%"
          borderRadius="full"
          bg={`${colorScheme}.300`}
        />
      )}
      
      <Box fontSize={{ base: "18px", md: "22px" }} mr={{ base: 3, md: 4 }} ml={isActive ? { base: 3, md: 4 } : { base: 1, md: 2 }}>  {/* Smaller icon and margins on mobile */}
        {icon}
      </Box>
      
      <Box textAlign="left">
        <Text color="white" fontWeight={isActive ? "bold" : "medium"} fontSize={{ base: "sm", md: "md" }}>  {/* Smaller font on mobile */}
          {label}
        </Text>
        <Text color="gray.400" fontSize={{ base: "xs", md: "sm" }}>  {/* Smaller font on mobile */}
          {description}
        </Text>
      </Box>
    </Box>
  );
};

function Navbar() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [hide, setHide] = useState(true);

  // Listen for global 'open-auth-modal' events (e.g. from guest CTAs)
  useEffect(() => {
    const handleOpenAuthModal = () => {
      setHide(true); // Ensure it shows sign-in view
      onOpen();
    };
    window.addEventListener('open-auth-modal', handleOpenAuthModal);
    return () => window.removeEventListener('open-auth-modal', handleOpenAuthModal);
  }, [onOpen]);

  // Auto-open sign-in modal after registration (from RegisterForm standalone page)
  useEffect(() => {
    const shouldOpenSignin = localStorage.getItem('burrow_open_signin');
    if (shouldOpenSignin) {
      localStorage.removeItem('burrow_open_signin');
      setHide(true); // Ensure it shows sign-in view
      // Small delay so the page finishes rendering first
      setTimeout(() => onOpen(), 300);
    }
  }, [onOpen]);
  const [modesMenuOpen, setModesMenuOpen] = useState(false);
  const [communityModalOpen, setCommunityModalOpen] = useState(false);

  // Listen for global 'open-modes-drawer' events (e.g. from Footer menu button)
  useEffect(() => {
    const handleOpenModesDrawer = () => setModesMenuOpen(true);
    window.addEventListener('open-modes-drawer', handleOpenModesDrawer);
    return () => window.removeEventListener('open-modes-drawer', handleOpenModesDrawer);
  }, []);
  
  // Search functionality state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchDropdownRef = useRef(null);
  
  const cancelRef = useRef(); // Add useRef hook for cancelRef
  const modeMenuRef = useRef(null);
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isOnChatPage = location.pathname === '/chat';
  const isOnVideoPage = /^\/[a-f0-9]{8}-/.test(location.pathname) || /^\/[^/]+\/[^/]+$/.test(location.pathname);
  const isOnFeedPage = location.pathname === '/feed';
  
  // Listen for community modal open events
  useEffect(() => {
    const handleOpenCommunityModal = (event) => {
      if (event.detail) {
        setCommunityModalOpen(true);
      }
    };
    
    // Add event listener
    document.addEventListener('openCommunityModal', handleOpenCommunityModal);
    
    // Cleanup
    return () => {
      document.removeEventListener('openCommunityModal', handleOpenCommunityModal);
    };
  }, []);
  
  // Add a disclosure for the feedback modal
  const {
    isOpen: isFeedbackOpen,
    onOpen: onFeedbackOpen,
    onClose: onFeedbackClose
  } = useDisclosure();
  
  // For debugging purposes
  useEffect(() => {
    console.log('FeedbackModal disclosure state changed:', isFeedbackOpen);
  }, [isFeedbackOpen]);
  
  // Listen for report modal open events
  useEffect(() => {
    const handleOpenReportModal = (event) => {
      console.log('openReportModal event received in Navbar with detail:', event.detail);
      if (event.detail) {
        // Ensure the modal opens even if we're in a different component context
        setTimeout(() => {
          console.log('Opening feedback modal with a delay');
          onFeedbackOpen();
        }, 100);
      }
    };
    
    // Add event listener
    console.log('Adding openReportModal event listener in Navbar');
    document.addEventListener('openReportModal', handleOpenReportModal);
    
    // Cleanup
    return () => {
      document.removeEventListener('openReportModal', handleOpenReportModal);
    };
  }, [onFeedbackOpen]);
  
  // Add a disclosure for the user profile drawer
  const { 
    isOpen: isProfileOpen, 
    onOpen: onProfileOpen, 
    onClose: onProfileClose 
  } = useDisclosure();

  // Improve auth state detection
  const authState = useSelector(state => state.auth);
  const storedToken = localStorage.getItem("burrowtoken");
  const isLoggedIn = Boolean(authState?.isAuth && storedToken);

  // Notification counts for chat + community badges
  const [notifCounts, setNotifCounts] = useState({ unreadMessages: 0, friendsCount: 0 });
  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchCounts = async () => {
      try {
        const res = await apiClient.get('/api/user/notification-counts');
        if (res.data?.success) setNotifCounts(res.data);
      } catch (_) {}
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [isLoggedIn]);

  // Get movies from Redux for search
  const moviesData = useSelector(state => state.Mode?.dataSource?.movies || []);
  
  // Close dropdown when clicking outside
  useOutsideClick({
    ref: searchDropdownRef,
    handler: () => setShowSearchDropdown(false),
  });
  
  // Perform live search when query changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }
    
    // Debounce search
    const timerId = setTimeout(() => {
      performLiveSearch(searchQuery);
    }, 300);
    
    return () => clearTimeout(timerId);
  }, [searchQuery, moviesData]);
  
  // Function to perform live search
  const performLiveSearch = (query) => {
    if (!query.trim() || !Array.isArray(moviesData)) return;
    
    setIsSearching(true);
    const queryLower = query.toLowerCase().trim();
    
    // Search through videos
    const results = moviesData.filter(video => {
      // Search in title
      const titleMatch = video.title?.toLowerCase().includes(queryLower);
      
      // Search in description
      const descMatch = video.description?.toLowerCase().includes(queryLower) ||
                       video.content?.description?.toLowerCase().includes(queryLower);
      
      // Search in tags
      const tagsMatch = video.tags?.some(tag => {
        const tagValue = typeof tag === 'string' ? tag : (tag.name || '');
        return tagValue.toLowerCase().includes(queryLower);
      });
      
      return titleMatch || descMatch || tagsMatch;
    }).slice(0, 5); // Limit to top 5 results
    
    setSearchResults(results);
    setShowSearchDropdown(results.length > 0);
    setIsSearching(false);
  };
  
  // Search handlers
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSearchDropdown(false);
      navigate(`/finder?query=${encodeURIComponent(searchQuery)}`);
    }
  };
  
  const handleSearchResultClick = (videoId, videoTitle) => {
    setShowSearchDropdown(false);
    setSearchQuery('');
    navigate(buildVideoUrl(videoId, videoTitle));
  };
  
  const handleViewAllResults = () => {
    if (searchQuery.trim()) {
      setShowSearchDropdown(false);
      navigate(`/finder?query=${encodeURIComponent(searchQuery)}`);
    }
  };
  
  // Add a useEffect to verify token validity only once on mount
  // Removed continuous checking to improve performance
  useEffect(() => {
    verifyAuthState(authState, dispatch, logoutAction);
  }, []); // Empty dependency array - only run once on mount

  // Remove verbose debug logging in production
  if (process.env.NODE_ENV === 'development') {
    console.log('Auth state in Navbar:', { 
      authState, 
      storedToken: Boolean(storedToken),
      isLoggedIn,
      authUser: authState?.user,
      isAdmin: authState?.isAdmin
    });
  }

  
  // State to track window width for responsive behavior
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Listen for window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // State to track and animate the current mode button image
  const [currentMode, setCurrentMode] = useState("mode");
  
  // Determine which mode button image to show based on the current path
  useEffect(() => {
    const path = location.pathname;
    let newMode = "mode";
    
    if (path.includes("/films")) {
      newMode = "films";
    } else if (path.includes("/shorts")) {
      newMode = "shorts";
    } else if (path.includes("/clips")) {
      newMode = "clips";
    }
    
    if (currentMode !== newMode) {
      // For fade transition, we directly update the mode
      setCurrentMode(newMode);
    }
  }, [location.pathname, currentMode]);
  
  // Update this function to better handle modal switching
  const handelhideshow = () => {
    console.log(`Switching auth modal:`, { toSignin: !hide, previousState: hide });
    setHide(!hide);
  };

  // Custom close function that ensures we reset to signin when actually closing
  const handleAuthModalClose = () => {
    console.log("Closing auth modal and resetting to signin");
    setHide(true); // Reset to signin view when closing
    onClose();
  };

  // Update this function to include handling for the favorites and watch later menu options
  const handleMenuOptionClick = (path) => {
    // Close the modes menu if it's open
    if (modesMenuOpen) {
      setModesMenuOpen(false);
    }

    // If user is not logged in and is trying to access gated features, prompt sign-in
    const authGatedPaths = ['/view-queues', '/favorites', '/watch-later', '/video-history'];
    if (!isLoggedIn && authGatedPaths.includes(path)) {
      // Show sign-in prompt toast with context-specific message
      const messages = {
        '/view-queues': 'Please sign in to view your queues.',
        '/favorites': 'Please sign in to view your favorites.',
        '/watch-later': 'Please sign in to view your Watch Later list.',
        '/video-history': 'Please sign in to view your video history.'
      };
      toast({
        title: 'Sign in required',
        description: messages[path] || 'Please sign in to access this feature.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      // Open the auth modal (defaults to Sign In)
      onOpen();
      return; // Block navigation
    }
    
    // Check if trying to navigate to watch-later and ensure the reducer is loaded
    if (path === '/watch-later') {
      // Get the full Redux state
      const state = store.getState();
      // Check if the watchLater reducer is properly registered
      if (!state.watchLater) {
        toast({
          title: "Feature Not Available",
          description: "Watch Later feature is being initialized. Please try again in a moment.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return; // Prevent navigation
      }
    }
    
    // Navigate to the selected path
    navigate(path);
  };
  
  // Add click-outside detection
  useEffect(() => {
    function handleClickOutside(event) {
      if (modeMenuRef.current && !modeMenuRef.current.contains(event.target)) {
        setModesMenuOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modeMenuRef]);

  return (
    <Box width="100%" position="fixed" top={0} zIndex={1000}>
      {/* Main Navbar Row */}
      <Flex
        alignItems="center"
        gap={{ base: "1", md: "2" }}  // Smaller gap on mobile
        width="100%"
        background="#000"
        p={{ base: 1, md: 2 }}  // Smaller padding on mobile
        height={{ base: "48px", md: "80px" }}  // Compact on mobile to match smaller icons
        flexWrap="nowrap"  // Never wrap - keep logo and sign in on same row
      >
        {/* Video View Selector FAB - Absolutely first, no margin, always on the left */}
          <Box 
            className="modes-fab-container" 
            position="absolute"
            left={{ base: "8px", md: "20px" }}  // Much smaller left margin on mobile
            top={{ base: "8px", md: "50%" }}  // Top position on mobile, centered on desktop
            transform={{ base: "none", md: "translateY(-50%)" }}  // No transform on mobile
            zIndex={15}
            pl={{ base: 1, md: 2 }}     // Smaller left padding on mobile
          >
            <motion.div
              className="modes-fab"
              onClick={() => setModesMenuOpen(!modesMenuOpen)}
              role="button"
              aria-label="Video view modes selector"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              style={{ paddingLeft: '2px' }}  // Reduced additional padding
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentMode}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{ 
                    width: windowWidth < 768 ? '120px' : '250px',  // Compact on mobile to leave room for icons
                    height: windowWidth < 768 ? '28px' : '50px',   // Smaller height on mobile
                    position: 'relative' 
                  }}
                >
                  {/* OWNER TRIAL 9-5: pulsing header logo swapped from the ROKU leftover PNG to the
                      Burrow long white SVG. REVERT: git checkout -- src/componants/Home/Navbar/Navbar.jsx
                      (and remove public/media/burrow-long-white.svg). */}
                  <motion.img
                    src={`${process.env.PUBLIC_URL}/media/burrow-long-white.svg`}
                    alt="Video mode selector"
                    style={{
                      width: windowWidth < 768 ? '100%' : 'auto',
                      height: windowWidth < 768 ? 'auto' : '100%',
                      display: 'block',
                      margin: '0 auto',
                      maxWidth: '100%',
                    }}
                    animate={{ 
                      scale: [1, 1.04, 1],
                      transition: { 
                        repeat: Infinity, 
                        duration: 2.3,
                        ease: "easeInOut" 
                      }
                    }}
                  />

                </motion.div>
              </AnimatePresence>
            </motion.div>
          </Box>

        {/* Desktop search bar — only on non-chat, non-video pages */}
        {!isOnChatPage && !isOnVideoPage && (
        <Box 
          display={{ base: "none", md: "flex" }}  // Hide on mobile
          justifyContent="center"
          alignItems="center"
          ref={searchDropdownRef}
          // Pinned between the pulsing icon (left ~284px) and the right icon cluster
          // (cluster is ~350px wide, so true midpoint sits 33px left of bar-center).
          position="absolute"
          left="50%"
          top="50%"
          transform="translate(calc(-50% - 33px), -50%)"
          zIndex={5}
          minWidth={0}
          px={{ base: 1, md: 2 }}
        >
        <form onSubmit={handleSearch}>
          <InputGroup size="sm">
            <Input 
              placeholder="Search videos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg="white"
              color="gray.800"
              borderColor="gray.300"
              _hover={{ borderColor: "gray.400" }}
              _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px blue.400" }}
              width={{ base: "150px", md: "165px", lg: "250px" }}
            />
            <InputRightElement>
              {isSearching ? (
                <Spinner size="xs" color="blue.400" />
              ) : (
                <IconButton
                  aria-label="Search"
                  icon={<FaSearch />}
                  size="xs"
                  type="submit"
                  variant="ghost"
                />
              )}
            </InputRightElement>
          </InputGroup>
        </form>
        
        {/* Search Results Dropdown */}
        {showSearchDropdown && searchResults.length > 0 && (
          <Box
            position="absolute"
            top="calc(100% + 8px)"
            left={0}
            right={0}
            bg="white"
            borderRadius="md"
            boxShadow="2xl"
            border="1px"
            borderColor="gray.200"
            zIndex={100}
            maxH="400px"
            overflowY="auto"
            animation="fadeInDown 0.2s ease-in-out"
          >
            <List spacing={0}>
              {searchResults.map((video) => {
                const thumbnailUrl = video.thumbnail || video.thumbnailUrl || video.thumbnailURL || 
                  (video.thumbnails && video.thumbnails.length > 0 ? video.thumbnails[0].url : '');
                
                return (
                  <ListItem
                    key={video.id || video.videoId}
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    onClick={() => handleSearchResultClick(video.id || video.videoId, video.title)}
                    p={2}
                    borderBottom="1px"
                    borderColor="gray.100"
                    transition="background 0.2s"
                  >
                    <Flex align="center" gap={3}>
                      <Image
                        src={thumbnailUrl || "/placeholder-thumbnail.jpg"}
                        alt={video.title}
                        boxSize="60px"
                        objectFit="cover"
                        borderRadius="md"
                      />
                      <Box flex={1} overflow="hidden">
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="gray.800"
                          noOfLines={2}
                        >
                          {video.title}
                        </Text>
                        {video.duration && (
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            {Math.floor(video.duration / 60)}:{String(video.duration % 60).padStart(2, '0')}
                          </Text>
                        )}
                      </Box>
                    </Flex>
                  </ListItem>
                );
              })}
            </List>
            
            {/* View All Results Button */}
            <Box
              p={2}
              bg="gray.50"
              borderTop="1px"
              borderColor="gray.200"
              cursor="pointer"
              _hover={{ bg: "gray.100" }}
              onClick={handleViewAllResults}
            >
              <Flex align="center" justify="center" gap={2} color="blue.500">
                <Text fontSize="sm" fontWeight="medium">
                  View All Results
                </Text>
                <FaArrowRight size={12} />
              </Flex>
            </Box>
          </Box>
        )}
      </Box>
      )}
      
      {/* Navigation icons - shown for ALL users (signed in or not) */}
      <HStack className="navbar-icons-row" spacing={{ base: 0, md: 2 }} ml="auto" mr={{ base: "4px", md: "50px" }} align="center" flexShrink={0}>
          {/* Video icon — shows on chat page (all sizes) and video pages (mobile only) */}
          {(isOnChatPage || isOnVideoPage) && (
              <IconButton
                aria-label="Back to Videos"
                icon={<FaVideo />}
                variant="ghost"
                color="gray.300"
                fontSize={{ base: "24px", md: "20px" }}
                size={{ base: "md", md: "md" }}
                minW={{ base: "40px", md: "40px" }}
                h={{ base: "40px", md: "40px" }}
                _hover={{ color: "yellow.300", bg: "whiteAlpha.200" }}
                onClick={() => navigate('/home')}
                display={isOnVideoPage && !isOnChatPage ? { base: "inline-flex", md: "none" } : "inline-flex"}
              />
          )}

          {/* Feed icon */}
            <Box position="relative" display="inline-flex">
              <IconButton
                aria-label="The Feed"
                icon={<FaNewspaper />}
                variant="ghost"
                color={location.pathname === '/feed' ? 'yellow.300' : 'gray.300'}
                fontSize={{ base: "24px", md: "20px" }}
                size={{ base: "md", md: "md" }}
                minW={{ base: "40px", md: "40px" }}
                h={{ base: "40px", md: "40px" }}
                _hover={{ color: "yellow.300", bg: "whiteAlpha.200" }}
                onClick={() => { isLoggedIn ? navigate('/feed') : onOpen(); }}
              />
            </Box>

          {/* Chat icon — desktop: toggle panel, mobile: navigate to /chat */}
            <Box position="relative" display="inline-flex">
              <IconButton
                aria-label="Chat"
                icon={<FaComments />}
                variant="ghost"
                color="gray.300"
                fontSize={{ base: "24px", md: "20px" }}
                size={{ base: "md", md: "md" }}
                minW={{ base: "40px", md: "40px" }}
                h={{ base: "40px", md: "40px" }}
                _hover={{ color: "yellow.300", bg: "whiteAlpha.200" }}
                onClick={() => {
                  // >=768px (md) = desktop → open floating panel; <768px = mobile → navigate
                  if (window.innerWidth >= 768) {
                    dispatch(toggleChatPanel());
                  } else {
                    navigate('/chat');
                  }
                }}
              />
              {isLoggedIn && notifCounts.unreadMessages > 0 && (
                <Badge
                  position="absolute"
                  top="0px"
                  right="0px"
                  colorScheme="red"
                  borderRadius="full"
                  fontSize={{ base: "7px", md: "9px" }}
                  minW={{ base: "14px", md: "16px" }}
                  h={{ base: "14px", md: "16px" }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  pointerEvents="none"
                >
                  {notifCounts.unreadMessages > 99 ? '99+' : notifCounts.unreadMessages}
                </Badge>
              )}
            </Box>

          {/* Community icon */}
            <Box position="relative" display="inline-flex">
              <IconButton
                aria-label="Community"
                icon={<FaUsers />}
                variant="ghost"
                color="gray.300"
                fontSize={{ base: "24px", md: "20px" }}
                size={{ base: "md", md: "md" }}
                minW={{ base: "40px", md: "40px" }}
                h={{ base: "40px", md: "40px" }}
                _hover={{ color: "yellow.300", bg: "whiteAlpha.200" }}
                onClick={() => { isLoggedIn ? setCommunityModalOpen(true) : onOpen(); }}
              />
            </Box>

          {/* Bell / notifications icon */}
            <Box position="relative" display="inline-flex">
              <IconButton
                aria-label="Notifications"
                icon={<FaBell />}
                variant="ghost"
                color="gray.300"
                fontSize={{ base: "24px", md: "20px" }}
                size={{ base: "md", md: "md" }}
                minW={{ base: "40px", md: "40px" }}
                h={{ base: "40px", md: "40px" }}
                _hover={{ color: "yellow.300", bg: "whiteAlpha.200" }}
                onClick={() => {
                  if (!isLoggedIn) { onOpen(); return; }
                  toast({ title: 'Notifications coming soon!', status: 'info', duration: 2000 });
                }}
              />
              {isLoggedIn && notifCounts.unreadMessages > 0 && (
                <Badge
                  position="absolute"
                  top="0px"
                  right="0px"
                  colorScheme="orange"
                  borderRadius="full"
                  fontSize={{ base: "7px", md: "9px" }}
                  minW={{ base: "14px", md: "16px" }}
                  h={{ base: "14px", md: "16px" }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  pointerEvents="none"
                >
                  !
                </Badge>
              )}
            </Box>

          {/* Avatar / Login — logged in users get their avatar, guests get a login icon */}
          {isLoggedIn ? (
            <Avatar
              boxSize={{ base: "40px", md: undefined }}
              size={{ base: undefined, md: "lg" }}
              src={authState?.profile?.userAvatar || "https://bunny-burrow-pull-1.b-cdn.net/user-profile-images/image%20(16).png"}
              name={authState?.user?.burrowerId || "User"}
              cursor="pointer"
              onClick={onProfileOpen}
              ml={{ base: "2px", md: 0 }}
              _hover={{ transform: "scale(1.05)" }}
              transition="transform 0.1s"
            />
          ) : (
              <IconButton
                aria-label="Login"
                icon={<BsPerson />}
                variant="ghost"
                color="gray.300"
                fontSize={{ base: "24px", md: "20px" }}
                size={{ base: "md", md: "md" }}
                minW={{ base: "40px", md: "40px" }}
                h={{ base: "40px", md: "40px" }}
                _hover={{ color: "yellow.300", bg: "whiteAlpha.200" }}
                onClick={onOpen}
              />
          )}

          {/* Auth modals — always rendered so guests can sign in from any icon click */}
          {!isLoggedIn && (
            hide ? (
              <Signin
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={handleAuthModalClose}
                cancelRef={cancelRef}
                handelhideshow={handelhideshow}
              />
            ) : (
              <Signup
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={handleAuthModalClose}
                cancelRef={cancelRef}
                handelhideshow={handelhideshow}
              />
            )
          )}
        </HStack>
    </Flex>

    {/* Mobile: search bar (hidden on chat, video detail, and feed pages) */}
    {(isOnChatPage || isOnVideoPage || isOnFeedPage) ? null : (
    <Box 
      display={{ base: "block", md: "none" }}  // Only show on mobile
      width="100%"
      px={2}
      pt={2}
      pb={1}
      bg="#000"
      position="relative"
    >
        <form onSubmit={handleSearch}>
          <InputGroup size="sm">
            <Input 
              placeholder="Search videos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg="white"
              color="gray.800"
              borderColor="gray.300"
              _hover={{ borderColor: "gray.400" }}
              _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px blue.400" }}
              width="100%"
            />
            <InputRightElement>
              {isSearching ? (
                <Spinner size="xs" color="blue.400" />
              ) : (
                <IconButton
                  aria-label="Search"
                  icon={<FaSearch />}
                  size="xs"
                  type="submit"
                  variant="ghost"
                />
              )}
            </InputRightElement>
          </InputGroup>
        </form>
        
        {/* Mobile Search Results Dropdown */}
        {showSearchDropdown && searchResults.length > 0 && (
          <Box
            position="absolute"
            top="calc(100% + 4px)"
            left={2}
            right={2}
            bg="white"
            borderRadius="md"
            boxShadow="2xl"
            border="1px"
            borderColor="gray.200"
            zIndex={100}
            maxH="400px"
            overflowY="auto"
            animation="fadeInDown 0.2s ease-in-out"
          >
            <List spacing={0}>
              {searchResults.map((video) => {
                const thumbnailUrl = video.thumbnail || video.thumbnailUrl || video.thumbnailURL || 
                  (video.thumbnails && video.thumbnails.length > 0 ? video.thumbnails[0].url : '');
                
                return (
                  <ListItem
                    key={video.id || video.videoId}
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    onClick={() => handleSearchResultClick(video.id || video.videoId, video.title)}
                    p={2}
                    borderBottom="1px"
                    borderColor="gray.100"
                    transition="background 0.2s"
                  >
                    <Flex align="center" gap={3}>
                      <Image
                        src={thumbnailUrl || "/placeholder-thumbnail.jpg"}
                        alt={video.title}
                        boxSize="60px"
                        objectFit="cover"
                        borderRadius="md"
                      />
                      <Box flex={1} overflow="hidden">
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="gray.800"
                          noOfLines={2}
                        >
                          {video.title}
                        </Text>
                        {video.duration && (
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            {Math.floor(video.duration / 60)}:{String(video.duration % 60).padStart(2, '0')}
                          </Text>
                        )}
                      </Box>
                    </Flex>
                  </ListItem>
                );
              })}
            </List>
            
            {/* View All Results Button */}
            <Box
              p={2}
              bg="gray.50"
              borderTop="1px"
              borderColor="gray.200"
              cursor="pointer"
              _hover={{ bg: "gray.100" }}
              onClick={handleViewAllResults}
            >
              <Flex align="center" justify="center" gap={2} color="blue.500">
                <Text fontSize="sm" fontWeight="medium">
                  View All Results
                </Text>
                <FaArrowRight size={12} />
              </Flex>
            </Box>
          </Box>
        )}
      </Box>
      )}

      {/* Replace the top drawer with a left drawer */}
      <Drawer
        isOpen={modesMenuOpen}
        placement="left"
        onClose={() => setModesMenuOpen(false)}
        size={{ base: "xs", md: "sm" }}  // Smaller drawer on mobile
      >
        <DrawerOverlay backdropFilter="blur(2px)" />
        <DrawerContent 
          bg="gray.800" color="white"
          maxWidth={{ base: "280px", md: "380px" }}  // Smaller max width on mobile
        >
          <DrawerHeader borderBottomWidth="1px" borderColor="gray.700" py={{ base: 3, md: 3 }}>
            <Flex justifyContent="space-between" alignItems="center">
              <Heading size={{ base: "md", md: "lg" }} color="white">Video tools</Heading>
              <DrawerCloseButton color="white" size={{ base: "md", md: "lg" }} />
            </Flex>
          </DrawerHeader>
          <DrawerBody py={{ base: 4, md: 4 }} overflowY="auto">
            <VStack spacing={{ base: 3, md: 3.5 }} align="stretch">
              {/* Home option */}
              <ModeMenuItem 
                icon={<img src={`${process.env.PUBLIC_URL}/media/burrow-solo-white.png`} alt="" style={{ width: 22, height: 22, display: "inline-block", verticalAlign: "middle" }} />}
                label="Home"
                description="All Videos"
                colorScheme="teal"
                isActive={currentMode === "mode"}
                onClick={() => {
                  setCurrentMode("mode");
                  navigate('/home');
                  setModesMenuOpen(false);
                }}
              />
              
              {/* Films option */}
              <ModeMenuItem 
                icon={<FaFilm color="#4299E1" />}
                label="Films"
                description="15+ minutes"
                colorScheme="blue"
                isActive={currentMode === "films"}
                onClick={() => {
                  setCurrentMode("films");
                  navigate('/films');
                  setModesMenuOpen(false);
                }}
              />
              
              {/* Shorts option */}
              <ModeMenuItem 
                icon={<BsCameraVideoFill color="#FC8181" />}
                label="Shorts"
                description="5-15 minutes"
                colorScheme="pink"
                isActive={currentMode === "shorts"}
                onClick={() => {
                  setCurrentMode("shorts");
                  navigate('/shorts');
                  setModesMenuOpen(false);
                }}
              />
              
              {/* Clips option */}
              <ModeMenuItem 
                icon={<MdAppShortcut color="#9F7AEA" />}
                label="Clips"
                description="Under 5 minutes"
                colorScheme="purple"
                isActive={currentMode === "clips"}
                onClick={() => {
                  setCurrentMode("clips");
                  navigate('/clips');
                  setModesMenuOpen(false);
                }}
              />
              
              {/* Categories option - NEW */}
              <ModeMenuItem 
                icon={<FaSitemap color="#ED8936" />}
                label="Categories"
                description="Browse by topic"
                colorScheme="orange"
                isActive={location.pathname.startsWith('/categories')}
                onClick={() => {
                  navigate('/categories');
                  setModesMenuOpen(false);
                }}
              />

              {/* Divider above Video Finder */}
              <Divider my={{ base: 2, md: 1 }} borderColor="whiteAlpha.300" />

              {/* Video Finder option (moved here) */}
              <ModeMenuItem 
                icon={<ImSearch color="white" />}
                label="Video Finder"
                description="Search all videos"
                colorScheme="purple"
                isActive={location.pathname === '/finder'}
                onClick={() => {
                  navigate('/finder');
                  setModesMenuOpen(false);
                }}
              />

              {/* Divider above View Queues */}
              <Divider my={{ base: 2, md: 1 }} borderColor="whiteAlpha.300" />

              {/* My Queues option */}
              <ModeMenuItem 
                icon={<FaList color="white" />}
                label="My Queues"
                description="Watchlist & custom queues" 
                colorScheme="blue"
                isActive={location.pathname === '/view-queues'}
                onClick={() => handleMenuOptionClick('/view-queues')}
              />

              {/* Favorites option */}
              <ModeMenuItem 
                icon={<MdFavorite color="#E53E3E" />}
                label="Favorites"
                description="Your favorite videos" 
                colorScheme="red"
                isActive={location.pathname === '/favorites'}
                onClick={() => handleMenuOptionClick('/favorites')}
              />
              
              {/* Watch Later option */}
              <ModeMenuItem 
                icon={<FaClock color="white" />}
                label="Watch Later"
                description="Saved for later viewing"
                colorScheme="orange"
                isActive={location.pathname === '/watch-later'}
                onClick={() => handleMenuOptionClick('/watch-later')}
              />
              
              {/* Video History option - NEWLY ADDED */}
              <ModeMenuItem 
                icon={<FaHistory color="lightblue" />}
                label="Video History"
                description="Recently watched videos"
                colorScheme="cyan"
                isActive={location.pathname === '/video-history'}
                onClick={() => handleMenuOptionClick('/video-history')}
              />
              
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
  

      {/* Add admin panel component */}
      <AdminPanel />
      
      
      {/* Add the user profile drawer component with communityModal props */}
      <UserDrawer 
        isOpen={isProfileOpen} 
        onClose={onProfileClose} 
        communityModalOpen={communityModalOpen}
        setCommunityModalOpen={setCommunityModalOpen}
      />
      
      {/* Keep the CommunityModal at the Navbar level */}
      <CommunityModal 
        isOpen={communityModalOpen} 
        onClose={() => setCommunityModalOpen(false)} 
      />
      
      {/* Add the FeedbackModal directly in the Navbar for easier access */}
      <FeedbackModal 
        isOpen={isFeedbackOpen}
        onClose={() => {
          console.log('FeedbackModal closing');
          onFeedbackClose();
        }}
      />
    </Box>
  );
}

export default Navbar;
// dispatch(getusertoken({ userlogin, handelhideshow, onClose })).then(() => {
//   onClose();
// });
